import { supabase } from './supabase';
import { Attachment } from '../types';

export interface AttachmentUpload {
  file: File;
  ideaId?: string;
  taskId?: string;
}

// Upload a file to Supabase Storage and save metadata
export const uploadAttachment = async ({ file, ideaId, taskId }: AttachmentUpload): Promise<Attachment> => {
  try {
    // Get current authenticated user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Validate inputs
    if (!ideaId && !taskId) {
      throw new Error('Either ideaId or taskId must be provided');
    }
    if (ideaId && taskId) {
      throw new Error('Cannot attach to both idea and task');
    }

    // File validation
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
      'application/pdf', 'text/plain', 'text/csv',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/zip', 'application/x-zip-compressed'
    ];
    
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      throw new Error(`File type ${file.type} is not allowed. Allowed types: images, PDFs, documents, spreadsheets, presentations, and ZIP files.`);
    }
    
    if (file.size > maxSize) {
      throw new Error(`File size ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds the 10MB limit.`);
    }

    // Generate unique file path: itemType/itemId/timestamp_filename
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const timestamp = Date.now();
    const uniqueFileName = `${timestamp}_${sanitizedFileName}`;

    const itemType = ideaId ? 'ideas' : 'tasks';
    const itemId = ideaId || taskId;
    const filePath = `${itemType}/${itemId}/${uniqueFileName}`;

    // Upload file to Supabase Storage
    const { error: storageError } = await supabase.storage
      .from('attachments')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false // Don't overwrite existing files
      });

    if (storageError) {
      console.error('Storage upload error:', storageError);
      throw new Error(`Upload failed: ${storageError.message}`);
    }

    // Save attachment metadata to database
    const attachmentData = {
      file_name: file.name,
      file_path: filePath,
      file_size: file.size,
      mime_type: file.type,
      uploaded_by: user.id,
      idea_id: ideaId || null,
      task_id: taskId || null
    };

    const { data: dbData, error: dbError } = await supabase
      .from('attachments')
      .insert(attachmentData)
      .select()
      .single();

    if (dbError) {
      console.error('Database insert error:', dbError);
      // Try to clean up uploaded file if database insert fails
      await supabase.storage.from('attachments').remove([filePath]);
      throw new Error(`Failed to save attachment metadata: ${dbError.message}`);
    }

    // Transform database row to frontend interface
    const attachment: Attachment = {
      id: dbData.id,
      fileName: dbData.file_name,
      filePath: dbData.file_path,
      fileSize: dbData.file_size,
      mimeType: dbData.mime_type,
      uploadedBy: dbData.uploaded_by,
      uploadedAt: new Date(dbData.uploaded_at),
      ideaId: dbData.idea_id || undefined,
      taskId: dbData.task_id || undefined
    };

    return attachment;
  } catch (error) {
    console.error('Upload attachment error:', error);
    throw error;
  }
};

// Get a temporary signed URL for downloading/viewing a file
export const getAttachmentUrl = async (filePath: string, expiresIn: number = 3600): Promise<string> => {
  try {
    const { data, error } = await supabase.storage
      .from('attachments')
      .createSignedUrl(filePath, expiresIn);

    if (error) {
      console.error('Get signed URL error:', error);
      throw new Error(`Failed to get file URL: ${error.message}`);
    }

    return data.signedUrl;
  } catch (error) {
    console.error('Get attachment URL error:', error);
    throw error;
  }
};

// Delete an attachment (both file and metadata)
export const deleteAttachment = async (attachment: Attachment): Promise<void> => {
  try {
    // Get current authenticated user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Delete from storage first
    const { error: storageError } = await supabase.storage
      .from('attachments')
      .remove([attachment.filePath]);

    if (storageError) {
      console.error('Storage delete error:', storageError);
      // Continue with database deletion even if storage fails
    }

    // Delete metadata from database
    const { error: dbError } = await supabase
      .from('attachments')
      .delete()
      .eq('id', attachment.id);

    if (dbError) {
      console.error('Database delete error:', dbError);
      throw new Error(`Failed to delete attachment metadata: ${dbError.message}`);
    }

    } catch (error) {
    console.error('Delete attachment error:', error);
    throw error;
  }
};

// Get all attachments for an idea or task
export const getAttachments = async (ideaId?: string, taskId?: string): Promise<Attachment[]> => {
  try {
    if (!ideaId && !taskId) {
      throw new Error('Either ideaId or taskId must be provided');
    }

    let query = supabase.from('attachments').select('*');
    
    if (ideaId) {
      query = query.eq('idea_id', ideaId);
    }
    if (taskId) {
      query = query.eq('task_id', taskId);
    }
    
    const { data, error } = await query.order('uploaded_at', { ascending: false });
    
    if (error) {
      console.error('Get attachments error:', error);
      throw new Error(`Failed to fetch attachments: ${error.message}`);
    }

    // Transform database rows to frontend interfaces
    const attachments: Attachment[] = data.map(row => ({
      id: row.id,
      fileName: row.file_name,
      filePath: row.file_path,
      fileSize: row.file_size,
      mimeType: row.mime_type,
      uploadedBy: row.uploaded_by,
      uploadedAt: new Date(row.uploaded_at),
      ideaId: row.idea_id || undefined,
      taskId: row.task_id || undefined
    }));

    return attachments;
  } catch (error) {
    console.error('Get attachments error:', error);
    throw error;
  }
};

// Utility function to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Utility function to get file type icon
export const getFileIcon = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📊';
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return '🗜️';
  if (mimeType.startsWith('text/')) return '📄';
  return '📎';
};

// Utility function to check if file is an image
export const isImageFile = (mimeType: string): boolean => {
  return mimeType.startsWith('image/');
}; 